/* eslint-disable no-template-curly-in-string */
import _ from 'lodash';
import React from 'react';

import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import useHttpStatus from '~/commons/hooks/useHttpStatus';
import PageHttp from '~/commons/components/PageHttp.jsx';

import FormGroup from '~/commons/components/FormGroup.jsx';
import FormCheckGroup from '~/commons/components/FormCheckGroup.jsx';

import { env } from '~/commons/config';

import { commaSeparatedToNumbers } from './utils';
import { tfyMeasureUnits, tfySqMeasureUnits } from '../../../../../../components/core/models/commons';
import { ColorViewer } from './tfy/FormPalette.jsx';
import FieldArrayCards from './FieldArrayCards.jsx';
import ListResource from '../lists/ListResource.jsx';

const shapes = [
  'square',
  'rectangle',
  'circle',
  'oval',
];

const ColorCards = ({ orgId }) => (
  <FieldArrayCards name={`blueprints.${orgId}.palette`}>
    {(s, i) => (
      <>
        <div className="row">
          <div className="col">
            <FormGroup
              name={`blueprints.${orgId}.palette.${i}.name`}
              label="Name"
              disabled
            />
          </div>
          <div className="col">
            <FormGroup
              name={`blueprints.${orgId}.palette.${i}.hex`}
              label="Hex"
              disabled
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <FormGroup
              name={`blueprints.${orgId}.palette.${i}.pomId`}
              label="Pom Id"
              disabled
            />
          </div>
          <div className="col">
            <FormGroup
              name={`blueprints.${orgId}.palette.${i}.code`}
              label="Color Code"
              disabled
            />
          </div>
        </div>
      </>
    )}
  </FieldArrayCards>
);

const CopyField = ({onCopy}) => { 
  const [orgId, setOrgId] = React.useState('');
  return (
    <div className="form-group">
      <label>Copy To</label>
      <div className="form-row">
        <div className="col">
          <input className='form-control' value={orgId} onChange={(e) => setOrgId(e.target.value)} />
        </div>
        <div className="col-auto">
          <button type='button' className='btn btn-primary' onClick={() => onCopy(orgId)} disabled={!Number(orgId)}>Copy</button>
        </div>
      </div>
    </div>
  );
}

const FormDesignTFY = ({ values, setValues, setAlert }) => {
  const orgIds = commaSeparatedToNumbers(values.tfyInfo?.orgId);

  const [resConfig, reqConfig] = useHttpStatus();
  const [resSupplier, reqSupplier] = useHttpStatus();
  React.useEffect(() => {
    reqConfig({ url: '/api/site/configs' });
    reqSupplier({ url: '/api/core/tfy/suppliers' });
  }, []);

  const replaceColors = (orgId, value, name) => setValues((v) => ({
    ...v ?? {},
    blueprints: {
      ...v?.blueprints ?? {},
      [orgId]: {
        ...v?.blueprints?.[orgId] ?? {},
        palette: _.uniqBy([
          ..._.castArray(value ?? []),
        ], (x) => x._id),
        paletteName: name,
      },
    },
  }));

  const addColors = (orgId, value) => setValues((v) => ({
    ...v ?? {},
    blueprints: {
      ...v?.blueprints ?? {},
      [orgId]: {
        ...v?.blueprints?.[orgId] ?? {},
        palette: _.uniqBy([
          ...v?.blueprints?.[orgId]?.palette ?? [],
          ..._.castArray(value ?? []),
        ], (x) => x._id),
      },
    },
  }));

  return (
    <PageHttp res={[resConfig, resSupplier]}>
      {([config, supplier]) => (
        <>
          <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">BASIC</h6>
          <div className="row">
            <div className="col">
              <div className="form-group">
                <label>Active</label>
                <FormCheckGroup name="tfyInfo.active" type="checkbox" label="" asSwitch />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <FormGroup name="tfyInfo.baseItemCode" label="Base Item Code" />
            </div>
            <div className="col">
              <FormGroup name="tfyInfo.orgId" label="Organization ID (Comma separated)" />
            </div>
          </div>
          <div className="row">
            <div className="col">
              <FormGroup name="tfyInfo.scale" label="Scale" />
            </div>
            <div className="col">
              <FormGroup name="tfyInfo.quality" label="Quality" />
            </div>
          </div>
          <Tabs id="design-tfy-tabs" unmountOnExit>
            {_.map(orgIds, (orgId) => (
              <Tab key={orgId} eventKey={`${orgId}`} title={`${orgId}`}>
                <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">DETAILS</h6>
                <div className="row">
                  <div className="col">
                    <div className="form-group">
                      <label>Manual Approval</label>
                      <FormCheckGroup name={`blueprints.${orgId}.manualApproval`} type="checkbox" label="" asSwitch />
                    </div>
                  </div>
                  <div className="col">
                    <CopyField onCopy={(toOrgId) => {
                      if (orgId === toOrgId) return;
                      setValues((v) => ({
                        ...v ?? {},
                        blueprints: {
                          ...v?.blueprints ?? {},
                          [toOrgId]: {
                            ...v?.blueprints?.[toOrgId] ?? {},
                            ..._.omit(v?.blueprints?.[orgId] ?? {}, '_id', '__v', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy', 'deletedAt'),
                          },
                        },
                      }));
                      setAlert({ type: 'info', message: `Blueprint ${orgId} copied to ${toOrgId}` });
                    }} />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormGroup name={`blueprints.${orgId}.material`} label="General Material" />
                  </div>
                  <div className="col">
                    <FormGroup name={`blueprints.${orgId}.numberOfLayers`} type="number" label="Number of Layers" />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormGroup name={`blueprints.${orgId}.priceColor`} type="number" label="Color Price Markup" appendLabel="%" placeholder={_.get(config, 'tfy.priceColor')} />
                  </div>
                  <div className="col">
                    <FormGroup name={`blueprints.${orgId}.priceShape`} type="number" label="Shape Price Markup" appendLabel="%" placeholder={_.get(config, 'tfy.priceShape')} />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormGroup name={`blueprints.${orgId}.supplier._id`} label="Supplier" as="select">
                      <option value=""> - </option>
                      {_.map(_.sortBy(supplier.rows, 'name'), ({ _id, name }) => (
                        <option value={_id} key={_id}>{`${name}`}</option>
                      ))}
                    </FormGroup>
                  </div>
                  <div className="col">
                    <FormGroup name={`blueprints.${orgId}.currency`} label="Currency" as="select">
                      <option value=""> - </option>
                      {env.currencies.map(({ code, symbol }) => (
                        <option value={code} key={code}>
                          {`${_.startCase(code)} - ${symbol}`}
                        </option>
                      ))}
                    </FormGroup>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <FormGroup
                      name={`blueprints.${orgId}.unitPrice.retailPrice`}
                      label={`Unit Price`}
                      type="number"
                      step="any"
                      min={0}
                    />
                  </div>
                  <div className="col">
                    <FormGroup name={`blueprints.${orgId}.unitPrice.unit`} label="Price Unit" as="select">
                      <option value=""> - </option>
                      {tfySqMeasureUnits.map((unit) => (
                        <option value={unit} key={unit}>
                          {unit}
                        </option>
                      ))}
                    </FormGroup>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="form-group">
                      <label>Color Enable</label>
                      <FormCheckGroup name={`blueprints.${orgId}.colorEnable`} type="checkbox" label="" asSwitch />
                    </div>
                  </div>
                  <div className="col">
                    <div className="form-group">
                      <label>Fill Effect Enable</label>
                      <FormCheckGroup name={`blueprints.${orgId}.patternEnable`} type="checkbox" label="" asSwitch />
                    </div>
                  </div>
                </div>
                {!_.get(values, `blueprints.${orgId}.patternEnable`) && (
                  <div className="row">
                    <div className="col">
                      <FormGroup name={`blueprints.${orgId}.resize`} label="Resize" as="select">
                        <option value="none">None</option>
                        <option value="rescale">Rescale</option>
                        <option value="crop">Crop</option>
                      </FormGroup>
                    </div>
                    <div className="col">
                      <FormGroup name={`blueprints.${orgId}.rescaleLimit`} type="number" label="Rescale Limit" appendLabel="%" placeholder={_.get(config, 'tfy.rescaleLimit')} />
                    </div>
                    <div className="col">
                      <FormGroup name={`blueprints.${orgId}.cropLimit`} type="number" label="Crop Limit" appendLabel="%" placeholder={_.get(config, 'tfy.cropLimit')} />
                    </div>
                  </div>
                )}
                <div className="row">
                  <div className="col">
                    <div className="form-group">
                      <label>Shape Enable</label>
                      <FormCheckGroup name={`blueprints.${orgId}.shapeEnable`} type="checkbox" label="" asSwitch />
                    </div>
                  </div>
                  <div className="col">
                    <div className="form-group">
                      <label>Shape Customization Enable</label>
                      <FormCheckGroup name={`blueprints.${orgId}.shapeCustomizationEnable`} type="checkbox" label="" asSwitch />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="form-group">
                      <label>Shape</label><br />
                      <label>Order: {_.get(values, `blueprints.${orgId}.shape`)?.join(', ')}</label>
                      {_.map(shapes, x => (
                        <FormCheckGroup
                          key={x}
                          name={`blueprints.${orgId}.shape`}
                          type="checkbox"
                          label={_.upperFirst(x)}
                          value={x}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="col">
                    <FormGroup name={`blueprints.${orgId}.originalShape`} label="Original Shape" as="select">
                      <option value=""> - </option>
                      {_.map(shapes, x => (
                        <option value={x} key={x}>{_.upperFirst(x)}</option>
                      ))}
                    </FormGroup>
                  </div>
                </div>
                <Tabs id="design-tfy-blueprint-tabs" unmountOnExit>
                  <Tab eventKey='palette' title='Palette'>
                    <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">PALETTE</h6>
                    <div className="pt-3">
                      <div className="row">
                        <div className="col">
                          <ListResource
                            {...{
                              searchable: true,
                              api: '/api/core/tfy/palettes',
                              sort: { name: 1 },
                              actions: {
                                create: false,
                                remove: false,
                              },
                              onRowClick: (row) => {
                                replaceColors(orgId, row.colors, row.name);
                              },
                              cols: [
                                { key: 'name', sortable: true, children: 'Name' },
                              ],
                            }}
                          />
                        </div>
                        <div className="col">
                          <div className='font-weight-bold'>{_.get(values, `blueprints.${orgId}.paletteName`)}</div>
                          <ColorViewer colors={_.get(values, `blueprints.${orgId}.palette`)} />
                          <ColorCards orgId={orgId} />
                        </div>
                      </div>
                    </div>
                  </Tab>
                  <Tab eventKey='color' title='Color'>
                    <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">COLOR</h6>
                    <div className="pt-3">
                      <div className="row">
                        <div className="col">
                          <ListResource
                            {...{
                              searchable: true,
                              api: '/api/core/tfy/colors',
                              sort: { name: 1 },
                              actions: {
                                create: false,
                                remove: false,
                              },
                              onRowClick: (row) => {
                                addColors(orgId, [row]);
                              },
                              cols: [
                                { key: 'name', sortable: true, children: 'Name' },
                                { key: 'pomId', sortable: true, children: 'Pom Id' },
                                { key: 'code', sortable: true, children: 'Color Code' },
                                { key: 'hex', sortable: true, children: 'Hex' },
                              ],
                            }}
                          />
                        </div>
                        <div className="col">
                          <div className='font-weight-bold'>{_.get(values, `blueprints.${orgId}.paletteName`)}</div>
                          <ColorViewer colors={_.get(values, `blueprints.${orgId}.palette`)} />
                          <ColorCards orgId={orgId} />
                        </div>
                      </div>
                    </div>
                  </Tab>
                  <Tab eventKey='layerInfo' title='Layer Info'>
                    <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">LAYER INFO</h6>
                    {_.map(_.range(0, _.get(values, `blueprints.${orgId}.numberOfLayers`) || 0), (i) => (
                      <div key={i} className="card mb-3">
                        <div className="card-body">
                          <h6 className="small border-bottom text-muted py-1 mb-3">LAYER {i + 1}</h6>
                          <div className="row">
                            <div className="col">
                              <FormGroup name={`blueprints.${orgId}.layerInfo.${i}.cutting`} label="Cutting" />
                            </div>
                            <div className="col">
                              <FormGroup name={`blueprints.${orgId}.layerInfo.${i}.material`} label="Material" />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <FormGroup name={`blueprints.${orgId}.layerInfo.${i}.defaultColor`} label="Default Color" as="select">
                                <option value=""></option>
                                {_.map(_.orderBy(_.get(values, `blueprints.${orgId}.palette`), 'code'), (x) => (
                                  <option key={x._id} value={x.name}>{x.code} - {x.name}</option>
                                ))}
                              </FormGroup>
                            </div>
                            <div className="col">
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </Tab>
                  {!_.get(values, `blueprints.${orgId}.patternEnable`) && (
                    <Tab eventKey='blueprintInfo' title='Standard Size'>
                      <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">STANDARD SIZE</h6>
                      <FieldArrayCards
                        name={`blueprints.${orgId}.standardSize`}
                        push={{}}
                        pushTitle='Add Standard Size'
                      >
                        {(s, i) => (
                          <>
                            <div className="row">
                              <div className="col">
                                <FormGroup name={`blueprints.${orgId}.standardSize.${i}.itemCode`} label="Item Code" />
                              </div>
                              <div className="col">
                                <FormGroup name={`blueprints.${orgId}.standardSize.${i}.gdn`} label="GDN" />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormGroup
                                  name={`blueprints.${orgId}.standardSize.${i}.retailPrice`}
                                  type="number"
                                  label="Retail Price"
                                  step="any"
                                  min={0}
                                />
                              </div>
                              <div className="col">
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormGroup name={`blueprints.${orgId}.standardSize.${i}.displaySize`} label="Display Size" />
                              </div>
                              <div className="col">
                                <FormGroup name={`blueprints.${orgId}.standardSize.${i}.unit`} label="Unit" as="select">
                                  <option value=""> - </option>
                                  {_.map(tfyMeasureUnits, (x) => (
                                    <option value={x} key={x}>{x}</option>
                                  ))}
                                </FormGroup>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormGroup name={`blueprints.${orgId}.standardSize.${i}.width`} type="number" label="Width" />
                              </div>
                              <div className="col">
                                <FormGroup name={`blueprints.${orgId}.standardSize.${i}.length`} type="number" label="Length" />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <FormGroup name={`blueprints.${orgId}.standardSize.${i}.scale`} label="Scale" />
                              </div>
                              <div className="col">
                                <FormGroup name={`blueprints.${orgId}.standardSize.${i}.quality`} label="Quality" />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <div className="form-group">
                                  <label>Item For TFY</label>
                                  <FormCheckGroup name={`blueprints.${orgId}.standardSize.${i}.itemForTFY`} type="checkbox" label="" asSwitch />
                                </div>
                              </div>
                              <div className="col">
                                <div className="form-group">
                                  <label>Item For Stock</label>
                                  <FormCheckGroup name={`blueprints.${orgId}.standardSize.${i}.itemForStock`} type="checkbox" label="" asSwitch />
                                </div>
                              </div>
                            </div>
                            <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">LAYER INFO</h6>
                            {_.map(_.range(0, _.get(values, `blueprints.${orgId}.numberOfLayers`) || 0), (j) => (
                              <div key={i} className="card mb-3">
                                <div className="card-body">
                                  <h6 className="small border-bottom text-muted py-1 mb-3">LAYER {j + 1}</h6>
                                  <div className="row">
                                    <div className="col">
                                      <FormGroup
                                        name={`blueprints.${orgId}.standardSize.${i}.layerInfo.${j}.img`}
                                        as="coverImg"
                                        api="/api/core/designs/files"
                                        label="Image"
                                      />
                                    </div>
                                    <div className="col">
                                      <FormGroup name={`blueprints.${orgId}.standardSize.${i}.layerInfo.${j}.percentage`} type="number" label="Percentage" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                            <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">COLORWAY</h6>
                            <FieldArrayCards
                              name={`blueprints.${orgId}.standardSize.${i}.colorways`}
                              push={{}}
                              pushTitle='Add Colorway'
                            >
                              {(t, j) => (
                                <>
                                  <div className="row">
                                    <div className="col">
                                      <FormGroup name={`blueprints.${orgId}.standardSize.${i}.colorways.${j}.name`} label="Name" />
                                    </div>
                                    <div className="col">
                                      <FormGroup
                                        name={`blueprints.${orgId}.standardSize.${i}.colorways.${j}.retailPrice`}
                                        type="number"
                                        label="Retail Price"
                                        step="any"
                                        min={0}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <FormGroup name={`blueprints.${orgId}.standardSize.${i}.colorways.${j}.itemCode`} label="Item Code" />
                                    </div>
                                    <div className="col">
                                      <FormGroup name={`blueprints.${orgId}.standardSize.${i}.colorways.${j}.gdn`} label="GDN" />
                                    </div>
                                  </div>
                                  <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">COLOR</h6>
                                  <div className="row row-cols-2">
                                    {_.map(_.range(0, _.get(values, `blueprints.${orgId}.numberOfLayers`) || 0), (k) => (
                                      <div key={i} className="col">
                                        <FormGroup name={`blueprints.${orgId}.standardSize.${i}.colorways.${j}.colors.${k}`} label={`Layer ${k + 1}`} as="select">
                                          <option value=""></option>
                                          {_.map(_.orderBy(_.get(values, `blueprints.${orgId}.palette`), 'code'), (x) => (
                                            <option key={x._id} value={x.name}>{x.code} - {x.name}</option>
                                          ))}
                                        </FormGroup>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </FieldArrayCards>
                          </>
                        )}
                      </FieldArrayCards>
                      <div className='mb-5' />
                    </Tab>
                  )}
                  {_.get(values, `blueprints.${orgId}.patternEnable`) && (
                    <Tab eventKey='blueprintInfo' title='Fill Effect'>
                      <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">FILL EFFECT</h6>
                      <div className="row">
                        <div className="col">
                          <FormGroup name={`blueprints.${orgId}.patternInfo.gdn`} label="GDN" />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormGroup name={`blueprints.${orgId}.patternInfo.fillMode`} label="Fill Mode" as="select">
                            <option value=""> - </option>
                            <option value='cropFromCenter'>Crop from center</option>
                            <option value='nearestUnit'>Nearest unit</option>
                          </FormGroup>
                        </div>
                        <div className="col">
                          <FormGroup name={`blueprints.${orgId}.patternInfo.unit`} label="Unit" as="select">
                            <option value=""> - </option>
                            {_.map(tfyMeasureUnits, (x) => (
                              <option value={x} key={x}>{x}</option>
                            ))}
                          </FormGroup>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormGroup name={`blueprints.${orgId}.patternInfo.unitWidth`} type="number" label="Unit Width" />
                        </div>
                        <div className="col">
                          <FormGroup name={`blueprints.${orgId}.patternInfo.unitLength`} type="number" label="Unit Length" />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <FormGroup name={`blueprints.${orgId}.patternInfo.defaultWidth`} type="number" label="Default Width" />
                        </div>
                        <div className="col">
                          <FormGroup name={`blueprints.${orgId}.patternInfo.defaultLength`} type="number" label="Default Length" />
                        </div>
                      </div>
                      <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">LAYER INFO</h6>
                      {_.map(_.range(0, _.get(values, `blueprints.${orgId}.numberOfLayers`) || 0), (j) => (
                        <div className="card mb-3">
                          <div className="card-body">
                            <h6 className="small border-bottom text-muted py-1 mb-3">LAYER {j + 1}</h6>
                            <div className="row">
                              <div className="col">
                                <FormGroup
                                  name={`blueprints.${orgId}.layerInfo.${j}.patternImg`}
                                  as="coverImg"
                                  api="/api/core/designs/files"
                                  label="Image"
                                />
                              </div>
                              <div className="col" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </Tab>
                  )}
                </Tabs>
              </Tab>
            ))}
          </Tabs>
        </>
      )}
    </PageHttp>
  );
};

export default FormDesignTFY;
